import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { workspaceService } from '../../../../services/workspaceService';
import type { CreateWorkspaceRequest } from '../../../../services/workspaceService';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';
import { workspaceListQueryKey } from '../../../../hooks/useWorkspaces';
import { useToast } from '../../../../hooks/useToast';
import type { Workspace } from '@mellify/shared';

/**
 * useCreateWorkspace
 *
 * Mutation hook that creates a new workspace, uploads logo if provided,
 * invalidates the workspace list cache, updates the store, and navigates
 * to the dashboard.
 */
export function useCreateWorkspace() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const addWorkspace = useWorkspaceStore((s) => s.addWorkspace);
  const setCurrentWorkspace = useWorkspaceStore((s) => s.setCurrentWorkspace);

  const createMutation = useMutation({
    mutationFn: async ({
      data,
      logoFile,
    }: {
      data: CreateWorkspaceRequest;
      logoFile?: File | null;
    }) => {
      // 1. Create the workspace
      const workspace = await workspaceService.create(data);

      // 2. Upload logo if provided
      if (logoFile && workspace.id) {
        try {
          const { logoUrl } = await workspaceService.uploadLogo(workspace.id, logoFile);
          return { ...workspace, logoUrl } as Workspace;
        } catch {
          // Logo upload failed, but workspace was created
          // Return workspace without logo rather than failing entirely
          return workspace;
        }
      }

      return workspace;
    },
    onSuccess: (workspace) => {
      // Add to store and set as current
      addWorkspace(workspace as Workspace);
      setCurrentWorkspace(workspace as Workspace);

      // Invalidate workspace list cache
      queryClient.invalidateQueries({ queryKey: workspaceListQueryKey });

      toast.success('Workspace created!', `"${workspace.name}" has been created successfully.`);

      // Navigate to dashboard
      navigate('/dashboard');
    },
    onError: (error: Error) => {
      toast.error('Failed to create workspace', error.message || 'Please try again.');
    },
  });

  return createMutation;
}